import { useApi } from './api';
import {
  toAgent,
  toCompany,
  toConversation,
  toConversationDetail,
  toDirectoryContact,
  toIntakeOption,
  toTemplate,
  type SpringPage,
  type WhatsAppAgentDto,
  type WhatsAppCompanyDto,
  type WhatsAppContactDto,
  type WhatsAppConversationDetailDto,
  type WhatsAppConversationSummaryDto,
  type WhatsAppIntakeOptionDto,
  type WhatsAppTemplateDto,
} from './whatsappAdapters';
import type {
  WhatsAppAgentSummary,
  WhatsAppApprovedTemplate,
  WhatsAppCompanyGroup,
  WhatsAppConversation,
  WhatsAppDirectoryContact,
  WhatsAppIntakeOption,
} from '../types/whatsapp';

export class WhatsAppApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

export interface WhatsAppConversationPage {
  items: WhatsAppConversation[];
  totalElements: number;
  totalPages: number;
  page: number;
}

export const useWhatsAppService = () => {
  const { apiFetch } = useApi();

  const request = async <T>(url: string, fallbackError: string, init?: RequestInit): Promise<T> => {
    const res = await apiFetch(`/whatsapp${url}`, init);
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const message =
        (body && typeof body === 'object' && (body.message || body.error)) ||
        (res.status === 403 ? 'No tienes permiso para esta acción.' : fallbackError);
      throw new WhatsAppApiError(String(message), res.status);
    }
    const text = await res.text();
    return (text ? JSON.parse(text) : null) as T;
  };

  const post = <T>(url: string, fallbackError: string, body?: unknown) =>
    request<T>(url, fallbackError, {
      method: 'POST',
      body: body === undefined ? undefined : JSON.stringify(body),
    });

  const put = <T>(url: string, fallbackError: string, body: unknown) =>
    request<T>(url, fallbackError, { method: 'PUT', body: JSON.stringify(body) });

  const listConversations = async (
    status: 'ALL' | 'NEW' | 'IN_PROGRESS' | 'WAITING' | 'CLOSED' = 'ALL',
    page = 0,
    size = 100
  ): Promise<WhatsAppConversationPage> => {
    const params = new URLSearchParams({ status, page: String(page), size: String(size) });
    const data = await request<SpringPage<WhatsAppConversationSummaryDto>>(
      `/conversations?${params}`,
      'No se pudieron cargar las conversaciones.'
    );
    return {
      items: data.content.map(toConversation),
      totalElements: data.totalElements,
      totalPages: data.totalPages,
      page: data.number,
    };
  };

  const detail = (d: WhatsAppConversationDetailDto) => toConversationDetail(d);

  const getConversation = async (id: string): Promise<WhatsAppConversation> =>
    detail(await request(`/conversations/${id}`, 'No se pudo cargar la conversación.'));

  const takeConversation = async (id: string) =>
    detail(await post(`/conversations/${id}/take`, 'No se pudo tomar la conversación.'));

  const assignConversation = async (id: string, agentUserId: string) =>
    detail(
      await post(`/conversations/${id}/assign`, 'No se pudo asignar la conversación.', {
        agentUserId: Number(agentUserId),
      })
    );

  const startOutbound = async (contactId: string, templateId: string) =>
    detail(
      await post('/conversations/outbound', 'No se pudo iniciar la conversación.', {
        contactId: Number(contactId),
        templateId: Number(templateId),
      })
    );

  const sendMessage = async (id: string, body: string) =>
    detail(await post(`/conversations/${id}/messages`, 'No se pudo enviar el mensaje.', { body }));

  const closeConversation = async (id: string, reason?: string) =>
    detail(await post(`/conversations/${id}/close`, 'No se pudo cerrar la conversación.', { reason }));

  const associateIntake = async (id: string, phoneCallId: string | number, createdFromWhatsApp = false) =>
    detail(
      await post(`/conversations/${id}/associate-intake`, 'No se pudo asociar el Intake.', {
        phoneCallId: Number(phoneCallId),
        createdFromWhatsApp,
      })
    );

  const updateNotes = async (id: string, internalNotes: string) =>
    detail(await put(`/conversations/${id}/notes`, 'No se pudieron guardar las notas.', { internalNotes }));

  const listContacts = async (): Promise<WhatsAppDirectoryContact[]> =>
    (await request<WhatsAppContactDto[]>('/contacts', 'No se pudieron cargar los contactos.')).map(
      toDirectoryContact
    );

  const assignContactAccount = async (contactId: string, accountId: string): Promise<WhatsAppDirectoryContact> =>
    toDirectoryContact(
      await put<WhatsAppContactDto>(`/contacts/${contactId}/account`, 'No se pudo asignar la empresa.', {
        accountId: Number(accountId),
      })
    );

  const updateContact = async (
    contactId: string,
    data: { displayName: string; email: string; accountId?: number | null }
  ): Promise<WhatsAppDirectoryContact> =>
    toDirectoryContact(
      await put<WhatsAppContactDto>(`/contacts/${contactId}`, 'No se pudieron guardar los datos del contacto.', data)
    );

  const listTemplates = async (): Promise<WhatsAppApprovedTemplate[]> =>
    (await request<WhatsAppTemplateDto[]>('/templates', 'No se pudieron cargar los mensajes iniciales.')).map(
      toTemplate
    );

  const listAgents = async (): Promise<WhatsAppAgentSummary[]> =>
    (await request<WhatsAppAgentDto[]>('/agents', 'No se pudieron cargar los agentes.')).map(toAgent);

  const listCompanies = async (): Promise<WhatsAppCompanyGroup[]> =>
    (await request<WhatsAppCompanyDto[]>('/companies', 'No se pudieron cargar las empresas.')).map(toCompany);

  const searchIntakes = async (search: string, accountId?: string | null): Promise<WhatsAppIntakeOption[]> => {
    const params = new URLSearchParams({ limit: '30' });
    if (search.trim()) params.append('search', search.trim());
    if (accountId) params.append('accountId', accountId);
    return (
      await request<WhatsAppIntakeOptionDto[]>(`/intakes?${params}`, 'No se pudieron buscar Intakes.')
    ).map(toIntakeOption);
  };

  return {
    listConversations,
    getConversation,
    takeConversation,
    assignConversation,
    startOutbound,
    sendMessage,
    closeConversation,
    associateIntake,
    updateNotes,
    listContacts,
    assignContactAccount,
    updateContact,
    listTemplates,
    listAgents,
    listCompanies,
    searchIntakes,
  };
};
